export interface OfficialMemorixSkill {
  name: string;
  content: string;
}

function skill(name: string, description: string, body: string[]): OfficialMemorixSkill {
  return {
    name,
    content: [
      '---',
      `name: ${name}`,
      `description: ${description}`,
      '---',
      '',
      ...body,
      '',
    ].join('\n'),
  };
}

export const OFFICIAL_MEMORIX_SKILLS: OfficialMemorixSkill[] = [
  skill(
    'memorix-memory',
    'Use when prior workspace context, past decisions, solved bugs, handoff state, or durable project knowledge would help a coding task.',
    [
      '# Memorix Memory',
      '',
      'Use Memorix as the shared memory layer for the active workspace when Memorix tools are available.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Starting or continuing a coding task and needing the best project map | `memorix_project_context` | `memorix context --task "<task>"` |',
      '| Broad continuation, memory overview, or "what do we know?" | `memorix_graph_context` | `memorix memory graph-context --query "<topic>"` |',
      '| Specific past decision, bug, file, or change | `memorix_search` | `memorix memory search --query "<topic>"` |',
      '| Need the full source for a search hit | `memorix_detail` | `memorix memory detail --id <id>` |',
      '| Need the sequence around one memory | `memorix_timeline` | `memorix memory timeline --id <id>` |',
      '| Learned reusable project knowledge | `memorix_store` | `memorix memory store --type <type> --entity <name> --title "<title>" "<text>"` |',
      '| Task or bug is complete/outdated | `memorix_resolve` | `memorix memory resolve --ids <ids>` |',
      '',
      '## Search Rules',
      '',
      '- Use `memorix_project_context` before broad continuation work or unfamiliar code changes. Start by reading its suggested files.',
      '- Use `memorix_search` for a specific decision, bug, file, or prior change that the project context did not answer.',
      '- Fetch detail before relying on a specific memory.',
      '- Treat current code-bound memory as a map. Treat stale, suspect, or unbound memory as a lead that must be verified against the current code.',
      '- Skip memory lookup for greetings, tiny one-off edits, or questions fully answered by the current file.',
      '- If a fresh project has no memories, proceed normally and do not repeat the same empty search in the same turn.',
      '',
      '## Autopilot Loop',
      '',
      '1. Get the project context when it helps.',
      '2. Read the suggested files/symbols before editing.',
      '3. Use stale or unbound memory only as a warning or search lead.',
      '4. Store durable decisions, fixes, and gotchas after the project state changes.',
      '5. Resolve completed or obsolete memories so future briefs stay small.',
      '',
      '## Store Rules',
      '',
      '| What to store | Type |',
      '|---|---|',
      '| Architecture or product decision | `decision` |',
      '| Bug and fix that may recur | `problem-solution` |',
      '| Non-obvious pitfall | `gotcha` |',
      '| How a subsystem works | `how-it-works` |',
      '| Important implementation change | `what-changed` |',
      '| Accepted compromise | `trade-off` |',
      '',
      '- Use concise titles, stable entity names, relevant `filesModified`, and `topicKey` for evolving topics.',
      '- Do not store secrets, credentials, raw private transcripts, trivial commands, or routine file reads.',
    ],
  ),
  skill(
    'memorix-reasoning',
    'Use when a technical decision, trade-off, rejected alternative, architecture rationale, or design risk should be recorded or recovered.',
    [
      '# Memorix Reasoning',
      '',
      'Use reasoning memory for why a decision was made. Use ordinary memory for what happened.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Store why a decision was made | `memorix_store_reasoning` | `memorix reasoning store --entity <name> --decision "<decision>" --rationale "<why>"` |',
      '| Find earlier rationale | `memorix_search_reasoning` | `memorix reasoning search --query "<topic>"` |',
      '| Need a stable evolving topic key | `memorix_suggest_topic_key` | `memorix memory suggest-topic-key --type decision --title "<title>"` |',
      '| Link rationale to commit evidence | `relatedCommits` on reasoning store | `memorix reasoning store ... --relatedCommits <sha>` |',
      '',
      '## Use Reasoning When',
      '',
      '- The answer depends on alternatives considered, constraints, expected outcomes, or accepted risks.',
      '- A future agent may otherwise reopen the same debate.',
      '- The user chose a direction and the reason matters more than the implementation details.',
      '- A Git Memory entry says what changed, but not why it changed.',
      '',
      '## Do Not Use Reasoning For',
      '',
      '- Simple progress notes.',
      '- Mechanical refactors with no decision.',
      '- Facts that belong in `memorix_store` as `how-it-works`, `what-changed`, or `problem-solution`.',
    ],
  ),
  skill(
    'memorix-sessions',
    'Use when resuming work, preparing handoff context, binding an HTTP control-plane project, or deciding whether session_start is useful.',
    [
      '# Memorix Sessions',
      '',
      '`memorix_session_start` is optional. Use it when session semantics help; do not make memory-only work heavier than needed.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Resume a prior long-running task | `memorix_session_start` | `memorix session start --agent <name>` |',
      '| HTTP control plane needs project binding | `memorix_session_start` with `projectRoot` | `memorix session start --projectRoot <absolute-path>` |',
      '| Need recent handoff/session summaries | `memorix_session_context` | `memorix session context` |',
      '| End a real working session | `memorix_session_end` | `memorix session end --sessionId <id> --summary "<summary>"` |',
      '| Need explicit subagent coordination identity | `memorix_session_start` with `joinTeam: true` | `memorix session start --joinTeam --agentType <agent>` |',
      '',
      '## Decision Rules',
      '',
      '- For normal memory search/store, do not require session start.',
      '- In HTTP mode, pass the absolute workspace path as `projectRoot` when available.',
      '- `projectRoot` is a detection anchor; Git identity is the final project identity.',
      '- Do not join coordination state unless the user is using tasks, messages, locks, handoff, or `memorix orchestrate`.',
    ],
  ),
  skill(
    'memorix-git-memory',
    'Use when the task depends on commit history, what changed, when a fix shipped, or linking engineering evidence to reasoning memory.',
    [
      '# Memorix Git Memory',
      '',
      'Use Git Memory for repository evidence. Use reasoning memory for intent.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Search what changed | `memorix_search` with a change-focused query | `memorix memory search --query "what changed <topic>"` |',
      '| Inspect a git-backed memory | `memorix_detail` | `memorix memory detail --id <id>` |',
      '| Ingest current commit manually | CLI | `memorix ingest commit` |',
      '| Ingest a specific commit | CLI | `memorix ingest commit --ref <ref>` |',
      '| Backfill recent history | CLI | `memorix ingest log --count 20` |',
      '| Install post-commit ingestion | CLI | `memorix git-hook --force` |',
      '',
      '## Retrieval Rules',
      '',
      '- Prefer Git Memory for "what changed", "when did this ship", "which files were touched", and "which commit introduced this".',
      '- Prefer reasoning memory for "why did we choose this", "what alternatives existed", and "what risk did we accept".',
      '- Cross-link important decisions with `relatedCommits` when storing reasoning.',
      '- Do not force-ingest noisy commits unless the skipped commit is genuinely important evidence.',
    ],
  ),
  skill(
    'memorix-mini-skills',
    'Use when durable project knowledge, gotchas, workflows, or repeated fixes should become reusable agent guidance instead of ordinary memory.',
    [
      '# Memorix Mini-Skills',
      '',
      'Promote memories into mini-skills only when they are reusable instructions, not just historical notes.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Preview/discover existing project skills | `memorix_skills` action `list` | `memorix skills list` |',
      '| Generate skills from memory patterns | `memorix_skills` action `generate` | `memorix skills generate` |',
      '| Write generated skills to an agent | `memorix_skills` action `generate`, `write: true` | `memorix skills generate --write --target codex` |',
      '| Promote selected memories permanently | `memorix_promote` action `promote` | `memorix memory promote --ids 42,43` |',
      '| List/delete promoted mini-skills | `memorix_promote` | `memorix memory promote --action list|delete` |',
      '',
      '## Promote When',
      '',
      '- The knowledge has been proven by repeated observations or a high-value fix.',
      '- It changes how future agents should act.',
      '- It is specific enough to be actionable and general enough to reuse.',
      '',
      '## Do Not Promote',
      '',
      '- Command logs, probes, stale memories, one-off status notes, or unverified guesses.',
      '- Anything that should expire after the current task.',
    ],
  ),
  skill(
    'memorix-orchestrate',
    'Use when a main agent needs Memorix to coordinate explicit subagent work through tasks, handoffs, messages, file locks, or the orchestrate CLI.',
    [
      '# Memorix Orchestrate',
      '',
      'Use Memorix coordination for explicit subagent workflows with joined identities, tasks, handoffs, messages, file locks, and orchestrated workers.',
      '',
      '## Tool Router',
      '',
      '| Situation | Prefer | CLI fallback |',
      '|---|---|---|',
      '| Run managed subagent execution | CLI | `memorix orchestrate --goal "<goal>" --agents claude:1,codex:1` |',
      '| Register coordination identity | `team_manage` action `join` | `memorix team join --agentType <agent>` |',
      '| Create or inspect tasks | `team_task` | `memorix task create|list|claim|complete` |',
      '| Send or read coordination messages | `team_message` | `memorix message send|broadcast|inbox` |',
      '| Share structured handoff | `memorix_handoff` | `memorix handoff send` |',
      '| Avoid file conflicts | `team_file_lock` | `memorix lock lock|unlock|status` |',
      '| Poll joined coordination state | `memorix_poll` | `memorix poll` |',
      '',
      '## Boundaries',
      '',
      '- For ordinary memory, do not join coordination state.',
      '- For production multi-agent execution, prefer `memorix orchestrate` so the main process owns planning, dispatch, retries, and verification gates.',
      '- `memorix orchestrate` uses the current checkout for single-worker runs and task worktrees for parallel runs. Use `--isolated` to force worktree isolation, `--no-worktree` to disable it, `--allow-dirty` to run with uncommitted changes, and `--no-auto-merge` to preserve task worktrees for manual review.',
      '- In worker prompts, use the worker agent ID returned by session start or team join; never use the coordinator ID as the worker identity.',
    ],
  ),
  skill(
    'memorix-troubleshooting',
    'Use when Memorix MCP, setup, project binding, HTTP control plane, hooks, skills, or agent integration is missing, stale, or failing.',
    [
      '# Memorix Troubleshooting',
      '',
      'Prefer direct CLI diagnostics first. MCP is an integration surface, not the only way to operate Memorix.',
      '',
      '## Diagnostic Router',
      '',
      '| Symptom | Check | CLI fallback |',
      '|---|---|---|',
      '| MCP tools missing | host MCP config and restart | `memorix setup --agent <agent>` |',
      '| Unsure project is bound | project status | `memorix status` and `git status` |',
      '| HTTP MCP stale or unbound | background health and explicit session bind | `memorix background status`; then `memorix session start --projectRoot <path>` |',
      '| Hooks not firing | hook status | `memorix hooks status` |',
      '| Need to inspect generated files | preview | `memorix hooks preview --agent <agent>` |',
      '| Setup looks incomplete | reinstall host integration | `memorix setup --agent <agent>` |',
      '| Need dashboard/control plane | background service | `memorix background start` or `memorix serve-http --port 3211` |',
      '',
      '## Mode Rules',
      '',
      '- Use stdio MCP (`memorix serve`) for normal host-launched MCP integration.',
      '- Use HTTP/background only for shared endpoint, dashboard, Docker, multiple clients, or foreground control-plane debugging.',
      '- On Windows, prefer absolute native paths for `projectRoot` and remember that visually similar junction paths may bind different session histories.',
      '- If MCP is unavailable but the CLI works, continue through CLI commands instead of blocking.',
    ],
  ),
];

export function getOfficialMemorixSkill(name: string): OfficialMemorixSkill | undefined {
  return OFFICIAL_MEMORIX_SKILLS.find((entry) => entry.name === name);
}
